import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Stack,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import {
    WhatsApp,
    Telegram,
    Instagram,
    Facebook,
    AutoAwesome,
    Speed,
    Security,
    CloudDone,
    CheckCircle,
    ArrowForward,
} from '@mui/icons-material';

const LandingPage = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: <WhatsApp sx={{ fontSize: 40, color: '#25D366' }} />,
            title: 'WhatsApp Automation',
            description: 'إدارة حسابات متعددة، إرسال رسائل جماعية، وردود تلقائية ذكية'
        },
        {
            icon: <Telegram sx={{ fontSize: 40, color: '#0088cc' }} />,
            title: 'Telegram Bots',
            description: 'إنشاء وإدارة بوتات تيليجرام بسهولة مع ردود آلية متقدمة'
        },
        {
            icon: <Instagram sx={{ fontSize: 40, color: '#E4405F' }} />,
            title: 'Instagram Marketing',
            description: 'جمع المتابعين، التعليقات، والمنشورات بشكل آلي'
        },
        {
            icon: <Facebook sx={{ fontSize: 40, color: '#1877F2' }} />,
            title: 'Facebook Scraping',
            description: 'استخراج بيانات المجموعات والصفحات بكفاءة عالية'
        },
        {
            icon: <AutoAwesome sx={{ fontSize: 40, color: '#FFD700' }} />,
            title: 'Chatbot AI',
            description: 'ردود تلقائية ذكية بناءً على كلمات مفتاحية مخصصة'
        },
        {
            icon: <Speed sx={{ fontSize: 40, color: '#FF6B6B' }} />,
            title: 'Campaigns',
            description: 'حملات تسويقية مجدولة عبر جميع المنصات'
        },
    ];

    const plans = [
        {
            name: 'Free',
            price: '0',
            period: 'مجاناً للأبد',
            features: [
                '100 رسالة شهرياً',
                'حساب واحد لكل منصة',
                'حملتان تسويقيتان',
                'دعم أساسي',
            ],
            color: '#757575',
            recommended: false,
        },
        {
            name: 'Pro',
            price: '29',
            period: 'شهرياً',
            features: [
                '5,000 رسالة شهرياً',
                '5 حسابات لكل منصة',
                '20 حملة تسويقية',
                'ردود تلقائية متقدمة',
                'إحصائيات مفصلة',
                'دعم ذو أولوية',
            ],
            color: '#1976d2',
            recommended: true,
        },
        {
            name: 'Enterprise',
            price: '99',
            period: 'شهرياً',
            features: [
                'رسائل غير محدودة',
                'حسابات غير محدودة',
                'حملات غير محدودة',
                'API مخصص',
                'تكامل مخصص',
                'مدير حساب مخصص',
                'دعم 24/7',
            ],
            color: '#9c27b0',
            recommended: false,
        },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Chip
                                label="🚀 منصة التسويق الذكي"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    mb: 2,
                                    fontWeight: 'bold',
                                }}
                            />
                            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
                                أتمتة التسويق عبر
                                <br />
                                جميع المنصات
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                إدارة WhatsApp، Telegram، Instagram، وFacebook من مكان واحد.
                                وفّر الوقت وزد المبيعات مع الأتمتة الذكية.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForward />}
                                    onClick={() => navigate('/register')}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#667eea',
                                        '&:hover': { bgcolor: '#f5f5f5' },
                                        px: 4,
                                        py: 1.5,
                                    }}
                                >
                                    ابدأ مجاناً
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate('/login')}
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                                    }}
                                >
                                    تسجيل الدخول
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 300,
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        borderRadius: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CloudDone sx={{ fontSize: 120, opacity: 0.5 }} />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h3" align="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
                    مميزات قوية لنمو أعمالك
                </Typography>
                <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
                    كل ما تحتاجه لإدارة حملاتك التسويقية بكفاءة
                </Typography>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    transition: 'transform 0.3s',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 },
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Pricing Section */}
            <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
                        خطط تناسب جميع الاحتياجات
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
                        ابدأ مجاناً وقم بالترقية عند الحاجة
                    </Typography>

                    <Grid container spacing={4} justifyContent="center">
                        {plans.map((plan, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        position: 'relative',
                                        border: plan.recommended ? `3px solid ${plan.color}` : 'none',
                                        transform: plan.recommended ? 'scale(1.05)' : 'none',
                                    }}
                                >
                                    {plan.recommended && (
                                        <Chip
                                            label="الأكثر شعبية"
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                bgcolor: plan.color,
                                                color: 'white',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ p: 4 }}>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            {plan.name}
                                        </Typography>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h3" fontWeight="bold" component="span">
                                                ${plan.price}
                                            </Typography>
                                            <Typography variant="body1" component="span" color="text.secondary">
                                                {' '}
                                                / {plan.period}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 3 }} />
                                        <List>
                                            {plan.features.map((feature, i) => (
                                                <ListItem key={i} sx={{ px: 0 }}>
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <CheckCircle sx={{ color: plan.color }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={feature} />
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Button
                                            variant={plan.recommended ? 'contained' : 'outlined'}
                                            fullWidth
                                            size="large"
                                            sx={{
                                                mt: 2,
                                                bgcolor: plan.recommended ? plan.color : 'transparent',
                                                borderColor: plan.color,
                                                color: plan.recommended ? 'white' : plan.color,
                                                '&:hover': {
                                                    bgcolor: plan.recommended ? plan.color : 'rgba(0,0,0,0.05)',
                                                },
                                            }}
                                            onClick={() => navigate('/register')}
                                        >
                                            ابدأ الآن
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 8,
                }}
            >
                <Container maxWidth="md">
                    <Typography variant="h3" align="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
                        جاهز للبدء؟
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ mb: 4, opacity: 0.9 }}>
                        انضم إلى مئات الشركات التي تستخدم منصتنا لتنمية أعمالها
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/register')}
                            sx={{
                                bgcolor: 'white',
                                color: '#667eea',
                                '&:hover': { bgcolor: '#f5f5f5' },
                                px: 5,
                                py: 1.5,
                            }}
                        >
                            ابدأ مجاناً الآن
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: 'background.paper', py: 4, borderTop: 1, borderColor: 'divider' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Social Auto
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                منصة الأتمتة الذكية لإدارة حملاتك التسويقية عبر جميع منصات التواصل الاجتماعي
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                روابط سريعة
                            </Typography>
                            <Stack spacing={1}>
                                <Button sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/pricing')}>
                                    الأسعار
                                </Button>
                                <Button sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/login')}>
                                    تسجيل الدخول
                                </Button>
                                <Button sx={{ justifyContent: 'flex-start' }} onClick={() => navigate('/register')}>
                                    إنشاء حساب
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                تواصل معنا
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Security sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    جميع البيانات محمية ومشفرة
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                        © 2025 Social Auto. جميع الحقوق محفوظة.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;
